/**
 * Typed access to the published Help articles, plus the unified Help search.
 *
 * `generated/help-articles.generated.ts` is written by `context/scripts/publish-help.mjs`
 * from `docs/help/*.md` and is committed. Nothing outside this module imports it
 * directly — same contract as `docs/glossary.ts`, so a change to the artifact's layout
 * lands in one place.
 *
 * Search lives HERE rather than in the page because it spans TWO corpora. An article and
 * a glossary term answer the same reader question ("what does verified mean") at
 * different lengths, so a search that only knew about one of them would look broken for
 * exactly the words the glossary exists to define.
 */
import { GlossaryEntry, CONSUMER_GLOSSARY } from 'docs/glossary'

import { HELP_ARTICLES as GENERATED_ARTICLES } from './generated/help-articles.generated'

export interface HelpArticle {
  /** Stable kebab-case slug. It is the route segment: `/help/<slug>`. */
  slug: string
  title: string
  /** One sentence, shown under the title in a list. Never the first line of the body. */
  summary: string
  /** Sort position. Lower is more prominent; drives `TOP_QUESTIONS`. */
  order: number
  /** Glossary ids this article covers — the link back from a term to its article. */
  terms: readonly string[]
  /** Markdown. Rendered by `pages/Help/ArticlePage`. */
  body: string
}

/**
 * Sorted by `order` HERE rather than trusting the artifact's own ordering.
 *
 * The publisher does emit them sorted, but every consumer below reads "first N by
 * order" as a promise about the DATA, not about the file — and the day someone appends
 * an article by hand or the publisher's sort changes, the failure is silent (a
 * plausible-looking but wrong Top questions list). Sorting 12 items costs nothing.
 */
export const HELP_ARTICLES: readonly HelpArticle[] = [
  ...(GENERATED_ARTICLES as readonly HelpArticle[]),
].sort((a, b) => a.order - b.order)

const BY_SLUG = new Map(HELP_ARTICLES.map(a => [a.slug, a]))

export const helpArticle = (slug: string): HelpArticle | undefined => BY_SLUG.get(slug)

/**
 * The five the Help index leads with.
 *
 * Five, not all twelve: the index's job is to answer the common question in one tap, and
 * a twelve-row list is something you read rather than scan. The rest are reachable
 * through search, and the glossary section below them is the other half of the index.
 */
export const TOP_QUESTIONS: readonly HelpArticle[] = HELP_ARTICLES.slice(0, 5)

/**
 * The article that best explains a glossary term, or `undefined`.
 *
 * The FIRST article whose `terms` names this id, in `order` — so "what is a certificate"
 * beats "if a certificate is not active yet" for `certificate`, which is the right answer
 * for someone who has just tapped an `(i)`. Resolved in one place because both the
 * TermSheet's "Learn more" and any future cross-link need the same answer.
 */
export const articleForTerm = (termId: string): HelpArticle | undefined =>
  HELP_ARTICLES.find(a => a.terms.includes(termId))

export type SearchResult =
  | { kind: 'article'; article: HelpArticle }
  | { kind: 'term'; entry: GlossaryEntry }

/** Nothing below this is worth showing; past it the reader is scrolling, not scanning. */
const MAX_RESULTS = 12

/**
 * One ranked list over both corpora.
 *
 * ## Matching
 *
 * Case-insensitive substring, no stemming and no fuzzy matching. Both corpora are tiny
 * (12 articles, 7 terms) and the vocabulary is fixed and short, so the failure a fuzzy
 * matcher protects against — a near-miss returning nothing — is rare here, while its own
 * failure (confident nonsense in a list the reader trusts) is not.
 *
 * ## Ranking, in three tiers
 *
 *   1. the NAME starts with the query — "tra" → "Transfer"
 *   2. the NAME contains it — "owner" → "Previous owners"
 *   3. only the prose contains it — body, summary, or a term's `short`
 *
 * A "name" is an article's title, or a term's term plus its aliases. Aliases rank with
 * the name deliberately: an alias IS a name for the entry, so someone typing "tap" (an
 * alias of Scan) has named the thing exactly and should not be ranked below an article
 * that merely mentions the word in a paragraph.
 *
 * Within a tier the order is: articles in `order`, then terms in artifact order. Stable
 * by construction — the tiers are separate passes over pre-ordered lists, so nothing
 * depends on sort stability. Articles lead because a question ("How do I check an item")
 * is more often what a searcher wants than the definition of a word in it; the term is
 * one row further down, not hidden.
 */
export const searchHelp = (query: string): SearchResult[] => {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const tiers: SearchResult[][] = [[], [], []]

  const push = (tier: number, result: SearchResult) => tiers[tier].push(result)

  /** Which tier this candidate lands in, or -1 for no match at all. */
  const rank = (names: string[], prose: string[]): number => {
    const lowered = names.map(n => n.toLowerCase())
    if (lowered.some(n => n.startsWith(q))) return 0
    if (lowered.some(n => n.includes(q))) return 1
    if (prose.some(p => p.toLowerCase().includes(q))) return 2
    return -1
  }

  for (const article of HELP_ARTICLES) {
    const tier = rank([article.title], [article.summary, article.body])
    if (tier >= 0) push(tier, { kind: 'article', article })
  }

  for (const entry of CONSUMER_GLOSSARY) {
    const tier = rank([entry.term, ...(entry.aliases ?? [])], [entry.short])
    if (tier >= 0) push(tier, { kind: 'term', entry })
  }

  return [...tiers[0], ...tiers[1], ...tiers[2]].slice(0, MAX_RESULTS)
}
